/*******************************************************************
* PAGE        : AddUpdatePromocode 
* REMARKS     :
* CREATED ON  : 27-08-2026
* AUTHOR      : Bamroliya.Paras
* MODIFY      : Bamroliya.Paras
* MODIFY ON   : 27-08-2026
********************************************************************/

import React, { useState } from 'react';
import DialogComponent from '../../components/DialogComponent/DialogComponent';
import InputComponent from '../../components/InputComponent/InputComponent';
import RadioboxComponent from '../../components/RadioboxComponent/RadioboxComponent';
import { CreatePromoCode } from '../../services/apiController';
import NotificationComponent from '../../components/NotificationComponent/NotificationComponent';

// #region Component
const AddUpdatePromocode = ({ openPopup, eventMasterId = 0, selectedPromoData = {}, handleClose }) => {

    // #region States
    const [loading, setLoading] = useState(false);

    const [errors, setErrors] = useState({}); // To store validation errors
    const [formData, setFormData] = useState({
        promoCode: selectedPromoData?.PromoCode || '',
        defaultPassword: selectedPromoData?.DefaultPassword || '',
        maxTicket: selectedPromoData?.MaxTicket ?? '',
        promoIsDefault: selectedPromoData?.IsDefault ?? false,
        promoIsActive: selectedPromoData?.IsActive ?? true,
    });

    // State to manage snackbar visibility and messages
    const [snackbarStates, setSnackbarStates] = useState({ isSnackbarOpen: false, snackbarMessage: '' });
    const handleCloseSnackbar = () => {
        setSnackbarStates({ isSnackbarOpen: false, snackbarMessage: '', snackbarSeverity: '' });
    };
    // #endregion

    // #region Methods
    const handleChange = (e, type, maxLength) => {
        const { name, value } = e.target;

        setFormData((prev) => ({ ...prev, [name]: value }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.promoCode.trim()) {
            newErrors.promoCode = "Please Enter Promocode";
        }

        if (!formData.defaultPassword.trim()) {
            newErrors.defaultPassword = "Please enter Default Password";
        }

        if (!formData.maxTicket || Number(formData.maxTicket) <= 0) {
            newErrors.maxTicket = "Please enter maximum limit";
        }

        setErrors(newErrors);

        // Focus first error field
        const firstErrorField = Object.keys(newErrors)[0];

        if (firstErrorField) {
            const element = document.querySelector(`[name="${firstErrorField}"]`);
            if (element) {
                element.focus();
                element.scrollIntoView({ behavior: "smooth", block: "center" });
            }
        }
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {

        if (!validateForm()) {
            return;
        }

        setLoading(true);
        // { "ticketMasterId": 1, "promoCode": "SUMMER2027", "defaultPassword": "startpass", "maxTicket": 100, "isDefault": false }
        // { "ticketMasterId": 1, "promoCode": "SUMMER2099", "defaultPassword": "startpass", "maxTicket": 100, "isDefault": false, "IsActive": true  }
        const promoParameter = {
            ticketMasterId: eventMasterId,
            promoCode: formData.promoCode,
            defaultPassword: formData.defaultPassword,
            maxTicket: formData.maxTicket,
            isDefault: formData.promoIsDefault,
            IsActive: formData.promoIsActive,
        };
        try {
            const response = await CreatePromoCode(promoParameter);
            if (response?.status) {
                setSnackbarStates({ isSnackbarOpen: true, snackbarMessage: response?.message || "Promocode create successfully", snackbarSeverity: "success" });
            } else {
                setSnackbarStates({ isSnackbarOpen: true, snackbarMessage: response?.message || "Unable to save promocode", snackbarSeverity: "error" });
            }
            handleClose();
        } catch (error) {
            setSnackbarStates({ isSnackbarOpen: true, snackbarMessage: "Something went wrong", snackbarSeverity: "error" });
        } finally {
            setLoading(false);
        }
    }

    // #endregion

    return (
        <React.Fragment>
            <NotificationComponent isOpen={snackbarStates.isSnackbarOpen} message={snackbarStates.snackbarMessage} onClose={handleCloseSnackbar} severity={snackbarStates.snackbarSeverity} />
            <DialogComponent
                isOpen={openPopup}
                Title="Promocode"
                ActionText={"Save"}
                IsLoading={loading}
                onSave={handleSubmit}
                onClose={handleClose}
            >
                <form onSubmit={handleSubmit}>
                    <InputComponent name="promoCode" placeholder="Promocode" value={formData.promoCode} onChange={handleChange} error={errors.promoCode} maxLength={25} /><br />
                    <InputComponent type="password" name="defaultPassword" placeholder="Default Password" value={formData.defaultPassword} onChange={handleChange} error={errors.defaultPassword} maxLength={25} /><br />
                    <InputComponent type='number' name="maxTicket" placeholder="Max Ticket" value={formData.maxTicket} onChange={handleChange} error={errors.maxTicket} maxLength={3} /><br />

                    <div style={{ marginTop: '20px', display: 'flex' }}>
                        <label style={{ width: '100%' }}>Is Default ?</label>

                        <RadioboxComponent
                            uniqueId="uniqueId-yes"
                            name="promoIsDefault"
                            value="true"
                            checked={formData.promoIsDefault === true}
                            onChange={(e) => setFormData(prev => ({ ...prev, promoIsDefault: e.target.value === 'true' }))}
                            label={<label htmlFor="uniqueId-yes" className="event-radio-card-label">
                                <span className="event-radio-card-text">Yes</span>
                            </label>}
                        />

                        <RadioboxComponent
                            uniqueId="uniqueId-no"
                            name="promoIsDefault"
                            value="false"
                            checked={formData.promoIsDefault === false}
                            onChange={(e) => setFormData(prev => ({ ...prev, promoIsDefault: e.target.value === 'true' }))}
                            label={<label htmlFor="uniqueId-no" className="event-radio-card-label">
                                <span className="event-radio-card-text">No</span>
                            </label>}
                        />
                    </div>

                    <div style={{ marginTop: '20px', display: 'flex' }}>
                        <label style={{ width: '100%' }}>Is Active ?</label>

                        <RadioboxComponent
                            uniqueId="promo-yes"
                            name="promoIsActive"
                            value="true"
                            checked={formData.promoIsActive === true}
                            onChange={(e) => setFormData(prev => ({ ...prev, promoIsActive: e.target.value === 'true' }))}
                            label={<label htmlFor="promo-yes" className="event-radio-card-label">
                                <span className="event-radio-card-text">Yes</span>
                            </label>}
                        />

                        <RadioboxComponent
                            uniqueId="promo-no"
                            name="promoIsActive"
                            value="false"
                            checked={formData.promoIsActive === false}
                            onChange={(e) => setFormData(prev => ({ ...prev, promoIsActive: e.target.value === 'true' }))}
                            label={<label htmlFor="promo-no" className="event-radio-card-label">
                                <span className="event-radio-card-text">No</span>
                            </label>}
                        />
                    </div>


                    <button type="submit" style={{ display: "none" }} />
                </form>

            </DialogComponent>
        </React.Fragment>
    );
};
// #endregion

export default AddUpdatePromocode;